import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, MapPin, Loader2, Building2, ChevronRight, Utensils, Wine, BedDouble } from "lucide-react";
import { api } from "../api";
import { resolveImageUrl } from "../lib/imageUrl";

const TYPE_META = {
  restaurant: { icon: Utensils, label: "Ristoranti", color: "#E11D48" },
  bar: { icon: Wine, label: "Bar", color: "#7C3AED" },
  hotel: { icon: BedDouble, label: "Hotel & B&B", color: "#0891B2" },
};

export default function Museum() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [places, setPlaces] = useState([]);

  useEffect(() => {
    if (!id) return;
    api.getMuseum(id).then((r) => { setData(r); setLoading(false); }).catch(() => setLoading(false));
    api.getMuseumPlaces(id).then((r) => setPlaces(r.items || [])).catch(() => {});
  }, [id]);

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-bg"><Loader2 className="text-brand animate-spin" /></div>;
  }
  const m = data.museum;
  const artworks = data.artworks || [];
  const heroSrc = resolveImageUrl(m.image_url);

  return (
    <div className="min-h-screen bg-bg page-enter">
      <div className="relative h-72">
        {heroSrc ? (
          <img
            src={heroSrc}
            alt={m.name}
            onError={(e) => { e.target.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-brand" style={{ zIndex: heroSrc ? -1 : 0 }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30" />
        <div className="relative z-10 px-5 pt-5">
          <Link to="/" className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm">
            <ChevronLeft className="text-white" size={20} />
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 text-white z-10">
          {m.kind === "city" && (
            <span className="inline-flex items-center gap-1 bg-gold/90 text-white px-2 py-1 rounded-full text-[10px] font-semibold tracking-widest mb-2">
              <MapPin size={10} />BORGO
            </span>
          )}
          <h1 className="font-heading font-bold text-4xl tracking-tight">{m.name}</h1>
          <div className="text-sm text-white/90 mt-1">
            {[m.city, m.region].filter(Boolean).join(" · ")}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6">
        {m.description && <p className="text-[15px] leading-relaxed text-ink mb-6">{m.description}</p>}

        <div className="text-[11px] tracking-[0.3em] font-semibold text-ink-soft mb-3">
          OPERE · {artworks.length}
        </div>
        {artworks.length === 0 ? (
          <div className="bg-white border border-border rounded-2xl p-6 text-center text-ink-soft">
            Nessuna opera disponibile
          </div>
        ) : (
          <div className="space-y-2">
            {artworks.map((a) => {
              const aSrc = resolveImageUrl(a.image_url);
              return (
                <Link
                  key={a.id}
                  to={`/opera/${a.id}`}
                  data-testid={`museum-artwork-${a.id}`}
                  className="flex items-center gap-3 bg-white border border-border rounded-2xl p-3 hover:border-brand/30 hover:shadow-sm transition group"
                >
                  {aSrc ? (
                    <img
                      src={aSrc}
                      alt={a.title}
                      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                  ) : null}
                  <div className={`w-16 h-16 rounded-xl bg-muted items-center justify-center ${aSrc ? "hidden" : "flex"}`}>
                    <Building2 className="text-ink-soft" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] text-ink truncate">{a.title}</div>
                    <div className="text-xs text-ink-soft truncate">{a.artist} · {a.year}</div>
                  </div>
                  <ChevronRight className="text-ink-soft group-hover:text-brand transition" size={18} />
                </Link>
              );
            })}
          </div>
        )}

        {places.length > 0 && (
          <>
            <div className="text-[11px] tracking-[0.3em] font-semibold text-ink-soft mt-8 mb-3">
              IN ZONA
            </div>
            <div className="space-y-2">
              {places.map((p) => {
                const meta = TYPE_META[p.type] || TYPE_META.restaurant;
                const Icon = meta.icon;
                return (
                  <div key={p.id} className="flex items-center gap-3 bg-white border border-border rounded-2xl p-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: meta.color }}>
                      <Icon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[15px] text-ink truncate">{p.name}</div>
                      <div className="text-xs text-ink-soft truncate">{p.address || meta.label}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
